import React, { useState, useEffect } from 'react';
import {
  ShoppingCart, Package, History, User, LogOut, Plus, Minus, Trash2,
  Image as ImageIcon, ShoppingBag, X, Menu, CheckCircle, FileText, FileSpreadsheet
} from 'lucide-react';
import api from '../../services/api';
import { useAlert } from '../../components/AlertProvider';
import BpuDetailedView from './components/ClientDetail/BpuDetailedView';

interface Consumable {
  id: number;
  title: string;
  description: string | null;
  base_price: number;
  custom_price?: number;
  discount_percentage?: number;
  has_custom_price?: boolean;
  currency: string;
  unit: string;
  images: string[] | null;
  stock_quantity: number | null;
}

interface CartItem {
  consumable: Consumable;
  quantity: number;
}

interface Order {
  id: number;
  order_number: string;
  status: string;
  total_amount: number;
  currency: string;
  created_at: string;
  order_date: string;
  received_at?: string;
  received_by_client_member_id?: number;
}

const ClientPortalTailwind: React.FC = () => {
  const { showSuccess, showError } = useAlert();
  const [catalog, setCatalog] = useState<Consumable[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [currentView, setCurrentView] = useState<'catalog' | 'cart' | 'orders' | 'bpu'>('catalog');
  const [showCheckout, setShowCheckout] = useState(false);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [showMenu, setShowMenu] = useState(false);

  const clientId = 1;
  const buyerName = "Mon Entreprise SA";

  useEffect(() => {
    void fetchCatalog();
    void fetchOrders();
  }, []);

  const fetchCatalog = async () => {
    try {
      // Use the Client's active pricing endpoint which already filters products and includes pricing
      const pricingRes = await api.get(`/client-pricing/buyer/${clientId}/active-products`);
      const products = Array.isArray(pricingRes.data) ? pricingRes.data : [];

      const catalogWithPrices = products.map((product: any) => {
        return {
          ...product,
          id: product.consumable_id,
          has_custom_price: true,
          currency: 'EUR' // Add default currency if not provided
        };
      });

      setCatalog(catalogWithPrices);
    } catch (error) {
      console.error('Erreur chargement catalogue:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await api.get('/purchase-orders', {
        params: { client_id: clientId }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Erreur chargement commandes:', error);
    }
  };

  const addToCart = (consumable: Consumable) => {
    const existing = cart.find(item => item.consumable.id === consumable.id);

    if (existing) {
      setCart(cart.map(item =>
        item.consumable.id === consumable.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { consumable, quantity: 1 }]);
    }
  };

  const updateQuantity = (consumableId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(consumableId);
    } else {
      setCart(cart.map(item =>
        item.consumable.id === consumableId
          ? { ...item, quantity }
          : item
      ));
    }
  };

  const removeFromCart = (consumableId: number) => {
    setCart(cart.filter(item => item.consumable.id !== consumableId));
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      const price = item.consumable.custom_price || item.consumable.base_price;
      return total + (price * item.quantity);
    }, 0);
  };

  const handleCheckout = async () => {
    try {
      const payload = {
        notes: deliveryNotes,
        items: cart.map(item => ({
          consumable_id: item.consumable.id,
          quantity: item.quantity,
        })),
      };

      const response = await api.post(`/purchase-orders?client_id=${clientId}`, payload);

      showSuccess('Commande créée avec succès ! Bon de commande généré.');
      setCart([]);
      setShowCheckout(false);
      setDeliveryNotes('');
      await fetchOrders();
      setCurrentView('orders');

      // If PDF was generated, show download option
      if (response.data.pdf_url) {
        setTimeout(() => {
          if (confirm('Voulez-vous télécharger le Bon de Commande maintenant ?')) {
            const link = document.createElement('a');
            link.href = response.data.pdf_url;
            link.target = '_blank';
            link.click();
          }
        }, 500);
      }
    } catch (error: any) {
      console.error('Erreur création commande:', error);
      showError(error.response?.data?.detail || 'Erreur lors de la création de la commande');
    }
  };

  const handleDownloadBdc = async (orderId: number, orderNumber: string) => {
    try {
      const response = await api.get(`/clients/me/orders/${orderId}/bdc/pdf`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = `BDC-${orderNumber}.pdf`;
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
      console.error('Erreur téléchargement BDC:', error);
      showError('Impossible de télécharger le bon de commande.');
    }
  };

  const handleConfirmReception = async (orderId: number) => {
    try {
      await api.post(`/purchase-orders/${orderId}/confirm-reception`);
      showSuccess('Réception confirmée avec succès !');
      await fetchOrders();
    } catch (error: any) {
      console.error('Erreur confirmation réception:', error);
      showError(error.response?.data?.detail || 'Erreur lors de la confirmation de réception');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-indigo-50/20 to-slate-50 dark:from-slate-950 dark:via-indigo-950/10 dark:to-slate-950">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <ShoppingBag className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold">{buyerName}</h1>
            <p className="text-xs text-indigo-100">Portail Achat</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setCurrentView('cart')}
            className="relative p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ShoppingCart className="h-6 w-6" />
            {cart.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold">
                {cart.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <button
          onClick={() => setCurrentView('catalog')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${currentView === 'catalog' ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
        >
          <Package className="h-4 w-4 inline mr-2" />
          Catalogue
        </button>
        <button
          onClick={() => setCurrentView('cart')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${currentView === 'cart' ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
        >
          <ShoppingCart className="h-4 w-4 inline mr-2" />
          Panier ({cart.length})
        </button>
        <button
          onClick={() => setCurrentView('orders')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${currentView === 'orders' ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
        >
          <History className="h-4 w-4 inline mr-2" />
          Mes commandes
        </button>
        <button
          onClick={() => setCurrentView('bpu')}
          className={`px-4 py-2 rounded-lg font-medium transition-all ${currentView === 'bpu' ? 'bg-indigo-600 text-white shadow-lg' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
        >
          <FileSpreadsheet className="h-4 w-4 inline mr-2" />
          BPU
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-6">
        {currentView === 'catalog' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {catalog.map((consumable, idx) => (
              <div
                key={consumable.id}
                className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 overflow-hidden hover:shadow-xl hover:border-indigo-300 dark:hover:border-indigo-700 transition-all duration-300 animate-in fade-in slide-in-from-bottom-4"
                style={{ animationDelay: `${idx * 30}ms`, animationFillMode: 'backwards' }}
              >
                <div className="w-full aspect-video bg-slate-100 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                  {consumable.images && consumable.images.length > 0 ? (
                    <img src={consumable.images[0]} alt={consumable.title} className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-16 w-16 text-slate-300" />
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-slate-900 dark:text-slate-100 mb-2 line-clamp-2">{consumable.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-3 line-clamp-2 min-h-[40px]">
                    {consumable.description || 'Aucune description'}
                  </p>

                  {consumable.has_custom_price ? (
                    <div className="mb-3">
                      <p className="text-xs text-slate-500 line-through">
                        Prix normal: {consumable.base_price.toFixed(2)} {consumable.currency}
                      </p>
                      <div className="flex items-center gap-2">
                        <p className="text-2xl font-bold text-indigo-600">
                          {consumable.custom_price?.toFixed(2)} {consumable.currency}
                        </p>
                        <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300 text-xs font-bold rounded-full">
                          -{consumable.discount_percentage?.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-indigo-600 mb-3">
                      {consumable.base_price.toFixed(2)} {consumable.currency}
                    </p>
                  )}

                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Unité: {consumable.unit}</p>

                  {consumable.stock_quantity !== null && (
                    <p className={`text-xs font-medium mb-3 ${consumable.stock_quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {consumable.stock_quantity > 0 ? `En stock: ${consumable.stock_quantity}` : 'Rupture de stock'}
                    </p>
                  )}

                  <button
                    onClick={() => addToCart(consumable)}
                    disabled={consumable.stock_quantity === 0}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors font-medium"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    Ajouter au panier
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {currentView === 'cart' && (
          <div>
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700">
                <ShoppingCart className="h-16 w-16 text-slate-300 mb-4" />
                <p className="text-lg font-medium text-slate-500 dark:text-slate-400 mb-4">Votre panier est vide</p>
                <button
                  onClick={() => setCurrentView('catalog')}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                >
                  Voir le catalogue
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Produit</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Prix unitaire</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Quantité</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Total</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cart.map((item) => {
                        const price = item.consumable.custom_price || item.consumable.base_price;
                        return (
                          <tr key={item.consumable.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <td className="py-4 px-4">
                              <div>
                                <p className="font-medium text-slate-900 dark:text-slate-100">{item.consumable.title}</p>
                                {item.consumable.has_custom_price && (
                                  <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300 text-xs font-medium rounded-full">
                                    -{item.consumable.discount_percentage?.toFixed(0)}%
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-4 px-4 text-right text-slate-600 dark:text-slate-400">
                              {price.toFixed(2)} {item.consumable.currency}
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => updateQuantity(item.consumable.id, item.quantity - 1)}
                                  className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                                <span className="w-12 text-center font-medium">{item.quantity}</span>
                                <button
                                  onClick={() => updateQuantity(item.consumable.id, item.quantity + 1)}
                                  className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                            <td className="py-4 px-4 text-right font-bold text-indigo-600">
                              {(price * item.quantity).toFixed(2)} {item.consumable.currency}
                            </td>
                            <td className="py-4 px-4 text-right">
                              <button
                                onClick={() => removeFromCart(item.consumable.id)}
                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded transition-colors"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-xl font-bold text-slate-900 dark:text-slate-100">Total</p>
                    <p className="text-3xl font-bold text-indigo-600">{calculateTotal().toFixed(2)} €</p>
                  </div>
                  <button
                    onClick={() => setShowCheckout(true)}
                    className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-bold text-lg"
                  >
                    Valider la commande
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {currentView === 'orders' && (
          <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">N° Commande</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Date</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Montant</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Statut</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="py-4 px-4 font-medium text-slate-900 dark:text-slate-100">{order.order_number}</td>
                    <td className="py-4 px-4 text-slate-600 dark:text-slate-400">
                      {new Date(order.order_date || order.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="py-4 px-4 text-right font-bold text-slate-900 dark:text-slate-100">
                      {order.total_amount.toFixed(2)} {order.currency}
                    </td>
                    <td className="py-4 px-4">
                      {order.received_at ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300">
                          <CheckCircle className="h-3 w-3" />
                          Reçue
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-950/30 dark:text-blue-300">
                          {order.status === 'delivered' ? 'Livrée' : order.status}
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-4 text-center">
                      <button
                        onClick={() => handleDownloadBdc(order.id, order.order_number)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-600 dark:bg-blue-950/30 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-950/50 text-xs font-medium rounded-lg transition-colors mr-2"
                      >
                        <FileText className="h-3 w-3" />
                        BDC
                      </button>
                      {order.status === 'delivered' && !order.received_at && (
                        <button
                          onClick={() => handleConfirmReception(order.id)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-lg transition-colors"
                        >
                          <CheckCircle className="h-3 w-3" />
                          J'ai bien reçu
                        </button>
                      )}
                      {order.received_at && (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          Confirmée le {new Date(order.received_at).toLocaleDateString('fr-FR')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {currentView === 'bpu' && (
          <div className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 p-6">
            <BpuDetailedView useMyEndpoint={true} />
          </div>
        )}
      </div>

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Finaliser la commande</h2>
              <button onClick={() => setShowCheckout(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-4 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg">
                <span className="text-slate-600 dark:text-slate-400">Total</span>
                <span className="text-2xl font-bold text-indigo-600">{calculateTotal().toFixed(2)} €</span>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">
                  Notes de livraison (optionnel)
                </label>
                <textarea
                  rows={3}
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  placeholder="Instructions spéciales, horaires de livraison..."
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setShowCheckout(false)}
                  className="flex-1 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCheckout}
                  className="flex-1 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors font-bold"
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Menu Sidebar */}
      {showMenu && (
        <div className="fixed inset-0 z-50 animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMenu(false)}></div>
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white dark:bg-slate-900 shadow-2xl animate-in slide-in-from-right duration-300">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-indigo-600">{buyerName[0]}</span>
                </div>
                <div>
                  <p className="font-bold text-slate-900 dark:text-slate-100">{buyerName}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Client</p>
                </div>
              </div>
            </div>
            <div className="p-4 space-y-2">
              <button
                onClick={() => { setCurrentView('orders'); setShowMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors text-left"
              >
                <History className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                <span className="font-medium text-slate-900 dark:text-slate-100">Mes commandes</span>
              </button>
              <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors text-left">
                <User className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                <span className="font-medium text-slate-900 dark:text-slate-100">Mon profil</span>
              </button>
              <div className="border-t border-slate-200 dark:border-slate-700 my-2"></div>
              <button className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors text-left text-red-600">
                <LogOut className="h-5 w-5" />
                <span className="font-medium">Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortalTailwind;
